import { MobergAnimationTimingMs } from "../../../../../../../../Moberg";
import { CPPOptData } from "../../../../Types/CPPOptPlot";
import { valueOrMissing } from "../../../../utils";
import { D3BoxPlotConfig } from "../../../D3/D3BoxPlot";
import { D3ClipPathConfig } from "../../../D3/D3ClipPath";
import { D3HorizontalLinesConfig } from "../../../D3/D3HorizontalLines";
import { D3RectConfig } from "../../../D3/D3Rect";
import { D3LinearXAxisConfig } from "../../../D3/D3LinearXAxis";
import { D3ParabolaConfig } from "../../../D3/D3Parabola";
import { D3SummaryValuesConfig } from "../../../D3/D3SummaryValues";
import { D3ValueAxisConfig } from "../../../D3/D3ValueAxis";
import { D3TimelineConfig } from "../../../D3/Timeline/D3Timeline";
import { ConfigurationBuilder } from "../../D3ConfigurationBuilder"
import { LEFT_MARGIN, RIGHT_MARGIN } from "../../Constants";
import { D3CPPOptDebugInfoConfig } from "./D3CPPOptDebugInfo";
import { D3ErrorBarsConfig } from "./D3CPPOptErrorBars";
import { D3CPPOptPlot } from "./D3CPPOptPlot";
import { D3LineConfig } from "../../../D3/D3Line";
import { getDataInView } from "../../../../Data/TimeSeriesData";
import { DataSource } from "../../../../Types/DataSource";
import { Analytic } from "../../../../Types/AnalysisDetails";

export class D3CPPOptPlotConfigurationBuilder extends ConfigurationBuilder<D3CPPOptPlot> {
	private bins = [42.5, 47.5, 52.5, 57.5, 62.5, 67.5, 72.5, 77.5, 82.5, 87.5, 92.5, 97.5, 102.5, 107.5, 112.5, 117.5]
	public transitionDuration: number = MobergAnimationTimingMs.SLOW
	private prxSignal: number[] = []
	private cppoptSignal: number[] = []


    getClipPathConfig = (): D3ClipPathConfig => {
		return {
			id: this.getClipPathId(),
			boundingBox: this.visualization.curveFitBoundingBox
		}
	}

    getTimelineConfig = (): D3TimelineConfig => {
		const timelineUpdateTimes = (start: number, end: number) => {
			this.visualization.viewTimesChanged(start, end)
			this.visualization.updateLinkedWindows()
		}

		return {
			id: this.visualization.config.id,
			viewScale: this.visualization.config.viewScale,
			width: this.visualization.config.dimensions.width - LEFT_MARGIN - RIGHT_MARGIN,
			annotations: [],
			fileScale: this.visualization.config.fileScale,
			playbackSpeed: this.visualization.config.playbackSpeed,
			currentTimelineController: this.visualization.config.timelineController,
			isLinked: this.visualization.config.isLinked,
			timeZone: this.visualization.config.timeZone,
			liveModeEnabled: this.visualization.config.liveModeEnabled,
			onDrag: this.visualization.onTimelineSliderDrag,
			onDragEnd: this.visualization.onTimelineSliderDragEnd,
			updateViewTimes: timelineUpdateTimes,
			goToStart: this.visualization.goToStart,
			goToEnd: this.visualization.goToEnd,
			goToNextPage: this.visualization.goToNextPage,
			goToPreviousPage: this.visualization.goToPreviousPage,
		}
	}

    getXAxisConfig = (): D3LinearXAxisConfig => ({
        scale: this.visualization.xScaleLinear,
        boundingBox: this.visualization.curveFitBoundingBox,
		label: "CPP (mmHg)",
    })

	getYAxisConfig = (): D3ValueAxisConfig => ({
		graphId: this.visualization.config.id,
		scale: this.visualization.yScale,
		label: "PRx",
		onDrag: this.visualization.onYAxisDrag,
		onDragEnd: this.visualization.onYAxisDragEnd,
		onDragStart: this.visualization.onYAxisDragStart,
		onDoubleClick: this.visualization.autoScale
	})

	getHorizontalLinesConfig = (): D3HorizontalLinesConfig => ({
		yScale: this.visualization.yScale,
		width: this.visualization.curveFitBoundingBox.width,
	})

	getParabolaConfig = (): D3ParabolaConfig => ({
		xScale: this.visualization.xScaleLinear,
		yScale: this.visualization.yScale,
		coefficients: this.visualization.cppOptData.coefficients,
		clipPathId: this.getClipPathId(),
		color: "var(--color-chart-axis-text, black)",
		isDashed: false,
		transitionDuration: this.transitionDuration,
		applyInverseFisher: true
	})

	getErrorBarsConfig = (): D3ErrorBarsConfig => ({
		yScale: this.visualization.yScale,
		xScale: this.visualization.xScaleLinear,
		clipPathId: this.getClipPathId(),
		bars: this.bins.map((x, index) => ({
			x,
			y: this.visualization.cppOptData.yValues[index],
			error: this.visualization.cppOptData.errors[index],
			includedInFit: this.visualization.cppOptData.includedInFit[index] === 1,
			dataPercentage: this.visualization.cppOptData.dataPercentages[index],
		})).filter(bar => bar.y != null),
		transitionDuration: this.transitionDuration
	})

	getDebugInfoConfig = (): D3CPPOptDebugInfoConfig => ({
		bins: this.bins,
		cppoptData: this.visualization.cppOptData
	})

	getPrxLineConfig = (): D3LineConfig => ({
		x1: 0,
		x2: this.visualization.xScaleLinear.range()[1],
		y1: this.visualization.yScale(this.visualization.config.prxThreshold),
		y2: this.visualization.yScale(this.visualization.config.prxThreshold),
		color: "gray",
		clipPathId: this.getClipPathId(),
		isDashed: false,
		transitionDuration: this.transitionDuration,
		strokeWidth: 1
	})

	getLLARectConfig = (): D3RectConfig => ({
		x: this.visualization.xScaleLinear(this.visualization.cppOptData.LLA ?? this.visualization.xScaleLinear.domain()[0]),
		y: 0,
		width: (this.visualization.cppOptData.LLA && this.visualization.cppOptData.CPPOpt) ? this.visualization.xScaleLinear(this.visualization.cppOptData.CPPOpt!) - this.visualization.xScaleLinear(this.visualization.cppOptData.LLA!) : 0,
		height: this.visualization.yScale.range()[0],
		color: "#EA00FF",
		clipPathId: this.getClipPathId(),
		transitionDuration: this.transitionDuration,
		opacity: 0.1
	})

	getULARectConfig = (): D3RectConfig => ({
		x: this.visualization.xScaleLinear(this.visualization.cppOptData.CPPOpt ?? this.visualization.xScaleLinear.domain()[0]),
		y: 0,
		width: (this.visualization.cppOptData.ULA && this.visualization.cppOptData.CPPOpt) ? this.visualization.xScaleLinear(this.visualization.cppOptData.ULA!) - this.visualization.xScaleLinear(this.visualization.cppOptData.CPPOpt!) : 0,
		height: this.visualization.yScale.range()[0],
		color: "#EA00FF",
		clipPathId: this.getClipPathId(),
		transitionDuration: this.transitionDuration,
		opacity: 0.1
	})

	getCPPoptLineConfig = (): D3LineConfig => ({
		x1: this.visualization.xScaleLinear(this.visualization.cppOptData.CPPOpt ?? this.visualization.xScaleLinear.domain()[0]),
		x2: this.visualization.xScaleLinear(this.visualization.cppOptData.CPPOpt ?? this.visualization.xScaleLinear.domain()[0]),
		y1: this.visualization.yScale.range()[0],
		y2: this.visualization.yScale.range()[1],
		color: "#EA00FF",
		isDashed: false,
		clipPathId: this.getClipPathId(),
		transitionDuration: this.transitionDuration,
		strokeWidth: 3
	})

	updateCPPOptData = (data: CPPOptData) => {
		this.visualization.cppOptData = data
	}

	getSummaryValuesConfig = (): D3SummaryValuesConfig => {
		const textColor = getComputedStyle(document.documentElement).getPropertyValue("--color-chart-axis-text").trim() || "black"
		return {
			x: 0,
			y: 0,
			width: this.visualization.config.dimensions.width/8,
			height: this.visualization.curveFitBoundingBox.height,
			values: [
				{
					name: "CPPopt",
					value: valueOrMissing(this.visualization.cppOptData.CPPOpt),
					color: "#EA00FF",
					precision: 0,
					isVisible: true,
				},
				{
					name: "LLA",
					value: valueOrMissing(this.visualization.cppOptData.LLA),
					color: textColor,
					precision: 0,
					isVisible: true,
				},
				{
					name: "ULA",
					value: valueOrMissing(this.visualization.cppOptData.ULA),
					color: textColor,
					precision: 0,
					isVisible: true,
				},
			],
			rows: 3,
			columns: 1,
		}
	}

	getPercentagesSummaryValuesConfig = (): D3SummaryValuesConfig => {
		const [startDate, endDate] = this.visualization.config.viewScale.domain()
		const totalDurationMinutes = (endDate.getTime() - startDate.getTime()) / (60 * 1000)
		const optAvailability = 100 * Math.min(1, this.cppoptSignal.length / totalDurationMinutes)
		const timeAbovePRxThreshold = 100 * Math.min(1, (this.prxSignal.filter(value => value > this.visualization.config.prxThreshold).length / totalDurationMinutes))
		const textColor = getComputedStyle(document.documentElement).getPropertyValue("--color-chart-axis-text").trim() || "black"
		
		return {
			x: 0,
			y: 0,
			width: this.visualization.config.dimensions.width/2,
			height: this.visualization.config.dimensions.height/8,
			values: [
				{
					name: "% time CPPopt calculable",
					value: optAvailability,
					color: textColor,
					precision: 0,
					isVisible: true,
					isPercent: true
				},
				{
					name: `% time > PRx threshold (${this.visualization.config.prxThreshold})`,
					value: timeAbovePRxThreshold,
					color: textColor,
					precision: 0,
					isVisible: true,
					isPercent: true
				},
			],
			rows: 1,
			columns: 2,
		}
	}

	getCPPoptBoxPlotConfig = (): D3BoxPlotConfig => ({
		color: "#EA00FF",
		boundingBox: this.visualization.cppoptBoxPlotBoundingBox,
		scale: this.visualization.cppoptBoxPlotYScale,
		yLabel: "CPPopt",
		title: this.visualization.config.cppoptAlgorithm === Analytic.CPPOpt ? "CPPopt" : "CPPopt (weighted)",
		viewScale: this.visualization.config.viewScale,
		precision: 0,
		values: this.cppoptSignal
	})

	getPRxBoxPlotConfig = (): D3BoxPlotConfig => ({
		color: "#FF0000",
		boundingBox: this.visualization.prxBoxPlotBoundingBox,
		scale: this.visualization.prxBoxPlotYScale,
		yLabel: "PRx",
		title: "PRx",
		viewScale: this.visualization.config.viewScale,
		precision: 1,
		values: this.prxSignal
	})

    private getClipPathId = () => `d3-clip-path-${this.visualization.config.id}`

	public readCPPoptSignal() {
		this.cppoptSignal = getDataInView(
			this.visualization.reactCallbacks.dataSourceMap.get(DataSource.CURRENT_PATIENT) as number,
			this.visualization.pageManager,
			{
				id: "CPPopt",
				name: "CPPopt",
				units: "mmHg",
				dataSource: DataSource.CURRENT_PATIENT,
				rawDataKey: "CPPopt",
				dataKey: "CPPOpt,Composite,SampleSeries",
				onDemandAnalysis: this.visualization.getCPPoptAnalysis(),
				isCompositePart: true,
				compositeIndex: 0
			},
			this.visualization.config.viewScale
		)
	}

	public readPRxSignal() {
		this.prxSignal = getDataInView(
			this.visualization.reactCallbacks.dataSourceMap.get(DataSource.CURRENT_PATIENT) as number,
			this.visualization.pageManager,
			{
				id: "PRx",
				name: "PRx",
				units: "",
				dataSource: DataSource.CURRENT_PATIENT,
				rawDataKey: "PRx",
				dataKey: "PRx",
				onDemandAnalysis: this.visualization.getPRxAnalysis(),
				isCompositePart: false
			},
			this.visualization.config.viewScale
		)
	}
}